import type { ProseSection } from "@/components/marketing/RichProse";
import type { FAQItem } from "@/components/marketing/FAQ";

/** Plain text for schema: strips [label](/path) markers to their labels. */
export const plain = (md: string) => md.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");

/** FAQ item whose visible answer keeps links while the schema answer stays plain. */
export const mdFaq = (q: string, md: string): FAQItem => ({ q, a: plain(md), aMd: md });

const TRADES: Record<string, { channels: string; trade: string; extra?: string }> = {
  roofing: {
    channels: "Local Services Ads and Google Ads",
    trade: "roofing companies",
    extra:
      "For one roofing contractor, two deals in a single month came to [$50,000 in closed work](/case-studies). In Arizona? See [Google Ads for roofing companies in Arizona](/google-ads-for-roofing-companies-arizona).",
  },
  painting: { channels: "Google Ads and Meta Ads", trade: "painting contractors" },
  "pool-service": { channels: "Google Ads and Meta Ads", trade: "pool service companies" },
  landscaping: {
    channels: "Google Ads",
    trade: "landscaping and hardscaping companies",
    extra:
      "For one landscaping and hardscaping client, a single lead from a campaign that looked expensive per lead [closed at $90,000](/case-studies).",
  },
  "general-contractors": { channels: "Google Ads and Meta Ads", trade: "general contractors" },
  "hardscape-drywall": { channels: "Google Ads and Meta Ads", trade: "hardscape and drywall contractors" },
  "home-services": { channels: "Google Ads", trade: "home services companies" },
  "tree-services": { channels: "Google Ads", trade: "tree service companies" },
  cleaning: { channels: "Google Ads", trade: "cleaning companies" },
  concrete: { channels: "Google Ads", trade: "concrete contractors" },
  electricians: { channels: "Google Ads and Local Services Ads", trade: "electricians" },
  plumbing: { channels: "Google Ads and Local Services Ads", trade: "plumbers" },
  hvac: {
    channels: "Google Ads and Local Services Ads",
    trade: "HVAC companies",
    extra: "In Arizona? See [Google Ads for HVAC companies in Arizona](/google-ads-for-hvac-companies-arizona).",
  },
  "pest-control": { channels: "Google Ads and Meta Ads", trade: "pest control companies" },
  remodeling: { channels: "Google Ads and Meta Ads", trade: "remodeling companies" },
  "window-replacement": { channels: "Google Ads and Meta Ads", trade: "window replacement companies" },
};

export const TRADE_WHO_SLUGS = Object.keys(TRADES);

export function tradeWhoFaq(slug: string): FAQItem | undefined {
  const t = TRADES[slug];
  if (!t) return undefined;
  return {
    q: `Who runs ${t.channels} for ${t.trade}?`,
    a: `Berk Hospitality Group does. We are a Scottsdale, Arizona agency and certified Google Partner, and we serve ${t.trade} in Arizona and across the US. Accounts are judged on booked jobs, not clicks.`,
  };
}

export function tradeWhoSection(slug: string): ProseSection | undefined {
  const faq = tradeWhoFaq(slug);
  if (!faq) return undefined;
  const extra = TRADES[slug]?.extra;
  return { heading: faq.q, paragraphs: extra ? [faq.a, extra] : [faq.a] };
}

const AZ_LINK = "See how we work with [home services companies across Arizona](/arizona-home-services-ppc-agency).";

export const HOME_WHO: ProseSection = {
  heading: "Who is Berk Hospitality Group?",
  paragraphs: [
    `Berk Hospitality Group is a marketing agency in Scottsdale, Arizona, a certified Google Partner and a Meta Business Partner. We run Google Ads, Local Services Ads and Meta Ads for home services companies in Arizona and across the US, and we build the SEO, AI search and automation behind them. ${AZ_LINK}`,
  ],
};

const CITY_TEXT = `Berk Hospitality Group is a Scottsdale, Arizona agency and certified Google Partner that runs Google Ads, Local Services Ads and Meta Ads for home services companies across the Phoenix metro. ${AZ_LINK}`;

export const cityWhoFaq = (city: string) => mdFaq(`Who manages PPC for home services companies in ${city}?`, CITY_TEXT);
export const cityWhoSection = (city: string): ProseSection => ({ heading: cityWhoFaq(city).q, paragraphs: [CITY_TEXT] });

const SERVICE_WHO: Record<string, { q: string; paragraphs: string[] }> = {
  "local-services-ads": {
    q: "Who manages Google Local Services Ads for a contractor in Arizona?",
    paragraphs: [
      "Usually the owner, someone in the office, or an agency. Berk Hospitality Group is a Scottsdale, Arizona agency and certified Google Partner that manages Local Services Ads for contractors and other home services businesses in Arizona and other states.",
      "Whoever manages it has five jobs: Google Verified screening, job types and service area, the weekly budget, rating bad leads, and review requests and answer rate. We handle all five, run Local Services Ads alongside Search in the same Google Ads account, and keep a written record of what was rated and credited.",
    ],
  },
  "google-ads": {
    q: "Who manages Google Ads for home services companies in Arizona?",
    paragraphs: [
      "Berk Hospitality Group is a Scottsdale, Arizona agency and certified Google Partner that manages Google Ads for home services companies in Arizona and across the US. Accounts are judged on booked jobs, not clicks.",
    ],
  },
  "meta-ads": {
    q: "Who runs Facebook and Instagram ads for home services companies?",
    paragraphs: [
      "Berk Hospitality Group runs Facebook and Instagram ads for home services companies and consumer brands from Scottsdale, Arizona. We are a Meta Business Partner, and we test hooks and offers each week against qualified calls and booked jobs, not clicks.",
    ],
  },
};

export const SERVICE_WHO_SLUGS = Object.keys(SERVICE_WHO);
export function serviceWhoFaq(slug: string): FAQItem | undefined {
  const s = SERVICE_WHO[slug];
  return s ? { q: s.q, a: s.paragraphs.join(" ") } : undefined;
}
export function serviceWhoSection(slug: string): ProseSection | undefined {
  const s = SERVICE_WHO[slug];
  return s ? { heading: s.q, paragraphs: s.paragraphs } : undefined;
}
